import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  makeStyles,
} from '@material-ui/core';
import * as React from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { useReactToPrint } from 'react-to-print';
import { RestaurantTable } from 'interfaces';

/**
 * The card that sits on the table. Scanning it opens the guest ordering app
 * on that exact table - the token IS the table, which is why it doubles as
 * proof the guest is sitting there.
 *
 * Printed at 300px so it survives being laminated and read across a table in
 * low light; error correction 'M' tolerates a bit of grease and wear.
 */
const GUEST_BASE = 'https://www.pekingmalta.com/table/#/';

const useStyles = makeStyles(() => ({
  card: {
    width: 340,
    margin: '0 auto',
    padding: 28,
    textAlign: 'center',
    border: '2px solid #2B2521',
    borderRadius: 14,
    background: '#fff',
    color: '#2B2521',
    fontFamily: "'Inter', system-ui, sans-serif",
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#8B7B6E',
  },
  table: {
    fontSize: 44,
    fontWeight: 800,
    lineHeight: 1.05,
    margin: '2px 0 14px',
    letterSpacing: '-0.03em',
  },
  how: { fontSize: 13, color: '#5C5048', marginTop: 14, lineHeight: 1.5 },
  brand: { marginTop: 16, fontSize: 12, color: '#F2333D', fontWeight: 700, letterSpacing: '0.04em' },
}));

interface Props {
  table: RestaurantTable | null;
  onClose: () => void;
}

export const TableQrDialog: React.FC<Props> = ({ table, onClose }) => {
  const classes = useStyles();
  const printRef = React.useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ content: () => printRef.current });

  if (!table) return null;
  const url = `${GUEST_BASE}${table.qrToken}`;

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent>
        <div ref={printRef}>
          <div className={classes.card}>
            <div className={classes.eyebrow}>Peking &middot; order from your table</div>
            <div className={classes.table}>{table.label}</div>
            <QRCodeCanvas value={url} size={300} level="M" includeMargin />
            <div className={classes.how}>
              Point your camera at the code, choose your dishes and send them.
              Your waiter brings them over.
            </div>
            <div className={classes.brand}>pekingmalta.com</div>
          </div>
        </div>
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ display: 'block', marginTop: 12, wordBreak: 'break-all' }}
        >
          {url}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" color="primary" onClick={handlePrint}>
          Print this card
        </Button>
      </DialogActions>
    </Dialog>
  );
};
